import { FormEvent, useState } from 'react';
import './RegisterPage.css';
import { Button, IconButton, InputAdornment, TextField } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import UsersService from '../../common/Rest/Services/UserService/UsersService';
import { RegisterData } from '../../common/Rest/Services/UserService/Data';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../common/Auth/AuthProvider';
import ROUTES from '../../common/Routing/Routes';
import {
  ValidateEmail,
  ValidatePassword,
  ValidateConfirmPassword,
  ValidatePhone,
  ValidateRequired,
} from '../../common/Utils/Utils';
import { AppConfig } from '../../../Config';
import GoogleIdentityButton from '../LoginPage/GoogleIdentityButton';
import GoogleAccountLinkPanel from '../LoginPage/GoogleAccountLinkPanel';

interface RegisterFormData extends RegisterData {
  confirmPassword: string;
}

function RegisterPage() {
  const [registerData, setRegisterData] = useState<RegisterFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    lastName: '',
    phoneNumber: '',
    nickname: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [pendingGoogleLink, setPendingGoogleLink] = useState<{
    idToken: string;
    maskedEmail?: string | null;
  } | null>(null);

  const { login } = useAuth();
  const navigate = useNavigate();
  const isGoogleLoginAvailable = Boolean(AppConfig.GoogleLoginClientId);

  const validateAllFields = () => {
    const newErrors: { [key: string]: string } = {};

    const emailValidation = ValidateEmail(registerData.email);
    if (!emailValidation.isValid) newErrors.email = emailValidation.validationMessage || '';

    const passwordValidation = ValidatePassword(registerData.password);
    if (!passwordValidation.isValid) newErrors.password = passwordValidation.validationMessage || '';

    const confirmPasswordValidation = ValidateConfirmPassword(registerData.password, registerData.confirmPassword);
    if (!confirmPasswordValidation.isValid) newErrors.confirmPassword = confirmPasswordValidation.validationMessage || '';

    const phoneValidation = ValidatePhone(registerData.phoneNumber);
    if (!phoneValidation.isValid) newErrors.phoneNumber = phoneValidation.validationMessage || '';

    const nameValidation = ValidateRequired(registerData.name);
    if (!nameValidation.isValid) newErrors.name = nameValidation.validationMessage || '';

    const lastNameValidation = ValidateRequired(registerData.lastName);
    if (!lastNameValidation.isValid) newErrors.lastName = lastNameValidation.validationMessage || '';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (event: FormEvent) => {
    event.preventDefault();
    setRegisterError(null);
    if (!validateAllFields()) return;

    const { confirmPassword, ...payload } = registerData;

    try {
      setSubmitting(true);
      setPendingGoogleLink(null);
      const response = await UsersService.register({
        ...payload,
        email: payload.email.trim(),
        phoneNumber: payload.phoneNumber.trim(),
        nickname: payload.nickname?.trim() || undefined,
      });
      login(response.token);
      navigate(ROUTES.PROFILE);
    } catch (error) {
      setRegisterError('Під час реєстрації сталася помилка. Можливо, такий email вже існує.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleCredential = async (idToken: string) => {
    try {
      setSubmitting(true);
      setRegisterError(null);
      setPendingGoogleLink(null);
      const response = await UsersService.loginWithGoogle({ idToken });
      if (response.requiresAccountLink) {
        setPendingGoogleLink({
          idToken,
          maskedEmail: response.maskedEmail,
        });
        return;
      }

      if (response.auth?.token) {
        login(response.auth.token);
        navigate(ROUTES.PROFILE);
        return;
      }

      setRegisterError('Google підтвердив реєстрацію, але Readerly не отримав токен.');
    } catch {
      setRegisterError('Не вдалося продовжити через Google. Спробуйте ще раз.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page default-container">
      <form className="register-page" onSubmit={handleRegister} noValidate>
        <div className="auth-heading">
          <h1>Реєстрація</h1>
          <p>Створіть акаунт, щоб зберігати список оренди, вішліст і адреси доставки.</p>
        </div>

        <TextField
          id="email"
          label="Email"
          type="email"
          value={registerData.email}
          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
          error={!!errors.email}
          helperText={errors.email}
          fullWidth
        />

        <div className="register-grid">
          <TextField
            id="name"
            label="Ім'я"
            value={registerData.name}
            onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
          />

          <TextField
            id="last-name"
            label="Прізвище"
            value={registerData.lastName}
            onChange={(e) => setRegisterData({ ...registerData, lastName: e.target.value })}
            error={!!errors.lastName}
            helperText={errors.lastName}
            fullWidth
          />
        </div>

        <TextField
          id="phone"
          label="Телефон"
          value={registerData.phoneNumber}
          onChange={(e) => setRegisterData({ ...registerData, phoneNumber: e.target.value })}
          error={!!errors.phoneNumber}
          helperText={errors.phoneNumber}
          fullWidth
        />

        <TextField
          id="nickname"
          label="Нікнейм (необов'язково)"
          value={registerData.nickname}
          onChange={(e) => setRegisterData({ ...registerData, nickname: e.target.value })}
          fullWidth
        />

        <div className="register-grid">
          <TextField
            id="password"
            label="Пароль"
            type={showPassword ? 'text' : 'password'}
            value={registerData.password}
            onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
            error={!!errors.password}
            helperText={errors.password}
            fullWidth
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Приховати пароль' : 'Показати пароль'}
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <TextField
            id="confirm-password"
            label="Повторіть пароль"
            type={showConfirmPassword ? 'text' : 'password'}
            value={registerData.confirmPassword}
            onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showConfirmPassword ? 'Приховати пароль' : 'Показати пароль'}
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            fullWidth
          />
        </div>

        {registerError && <div className="error-message">{registerError}</div>}

        {pendingGoogleLink && (
          <GoogleAccountLinkPanel
            idToken={pendingGoogleLink.idToken}
            maskedEmail={pendingGoogleLink.maskedEmail}
            onAuthenticated={(token) => {
              login(token);
              navigate(ROUTES.PROFILE);
            }}
            onCancel={() => setPendingGoogleLink(null)}
          />
        )}

        <Button id="register-button" variant="contained" type="submit" disabled={submitting} size="large">
          {submitting ? 'Створюємо акаунт...' : 'Зареєструватися'}
        </Button>

        {isGoogleLoginAvailable && (
          <>
            <div className="auth-divider">
              <span>або</span>
            </div>

            <GoogleIdentityButton
              disabled={submitting}
              text="signup_with"
              onCredential={handleGoogleCredential}
              onError={(message) => setRegisterError(message || null)}
            />
          </>
        )}

        <div className="auth-switch">
          Вже маєте акаунт? <NavLink to={ROUTES.LOGIN}>Увійти</NavLink>
        </div>
      </form>
    </div>
  );
}

export default RegisterPage;
